import React, { useState, useEffect } from 'react';
import { Settings, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminTradeManager() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => { fetchTrades(); }, []);

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trades`);
      setTrades(response.data);
    } catch { console.error('Failed to fetch trades'); }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/trades`, formData, { withCredentials: true });
      toast.success('Trade added!');
      setFormData({ name: '', description: '' });
      setShowForm(false);
      fetchTrades();
    } catch { toast.error('Failed to add trade'); }
  };

  const handleEdit = (trade) => {
    setEditing(trade.name);
    setFormData({ name: trade.name, description: trade.description || '' });
  };

  const handleSave = async (originalName) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/trades/${encodeURIComponent(originalName)}`, formData, { withCredentials: true });
      toast.success('Trade updated!');
      setEditing(null);
      setFormData({ name: '', description: '' });
      fetchTrades();
    } catch { toast.error('Failed to update trade'); }
  };

  const handleCancel = () => {
    setEditing(null);
    setFormData({ name: '', description: '' });
  };

  const handleDelete = async (tradeName) => {
    if (!confirm(`Delete "${tradeName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/trades/${encodeURIComponent(tradeName)}`, { withCredentials: true });
      toast.success('Trade deleted!');
      fetchTrades();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete trade'); }
  };

  if (loading) return <div className="animate-pulse h-32 bg-slate-100 rounded-xl" />;

  return (
    <div className="card" data-testid="trades-section">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="h-5 w-5 text-orange-500" /> Trades
        </h3>
        <button onClick={() => setShowForm(!showForm)} className="btn-accent text-sm flex items-center gap-2" data-testid="add-trade-btn">
          <Plus className="h-4 w-4" /> Add Trade
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="space-y-3 mb-4 pb-4 border-b border-slate-200" data-testid="trade-form">
          <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Trade name (e.g., Carpenter)" required className="w-full" data-testid="trade-name-input" />
          <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Description (optional)" rows={2} className="w-full" data-testid="trade-description-input" />
          <div className="flex gap-2">
            <button type="submit" className="btn-primary text-sm" data-testid="submit-trade-btn">Add</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm" data-testid="cancel-trade-btn">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2" data-testid="trades-list">
        {trades.map((trade) => (
          <div key={trade.trade_id || trade.name} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`trade-${trade.trade_id || trade.name}`}>
            {editing === trade.name ? (
              <div className="space-y-2">
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full text-sm" data-testid="edit-trade-name-input" />
                <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full text-sm" placeholder="Description (optional)" data-testid="edit-trade-desc-input" />
                <div className="flex gap-2">
                  <button onClick={() => handleSave(trade.name)} className="text-green-600 hover:text-green-700 p-1" data-testid="save-trade-btn"><Check className="h-4 w-4" /></button>
                  <button onClick={handleCancel} className="text-slate-600 hover:text-slate-700 p-1" data-testid="cancel-edit-trade-btn"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-slate-900">{trade.name}</p>
                  {trade.description && <p className="text-sm text-slate-600">{trade.description}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(trade)} className="text-blue-600 hover:text-blue-700 p-1" data-testid={`edit-trade-btn-${trade.name}`}><Edit className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(trade.name)} className="text-red-600 hover:text-red-700 p-1" data-testid={`delete-trade-btn-${trade.name}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
